import React, { useState, useEffect } from "react";
import Modal1 from "./Modal1";
import Icon1 from "../icons/Icon";
import PrimaryButton from "../buttons/PrimaryButton";
import SecondaryButton from "../buttons/SecondaryButton";
import DropDownGPT03 from "../dropdowns/DropDownGTP03";
import TextField2 from "../textfields/TextField2";
import EquipmentTypeSelect from "./EquipmentTypeSelect";
import SearchBarDocuments from "../textfields/SearchBarDocuments";

interface Equipment {
  name?: string;
  brand?: string;
  category?: string;
  label?: string;
  [key: string]: any;
}

interface EquipmentSelectionData {
  piece: string;
  equipment_type: string;
  customPieceName?: string;
}

interface EquipmentsCandidatesModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  equipments: Equipment[];
  pieceOptions: Array<{ label: string; value: string }>;
  onAddEquipments: (selectedEquipments: Array<{
    equipment: Equipment;
    selectionData: EquipmentSelectionData;
  }>) => Promise<void>;
  emptyMessage?: string;
  addButtonText?: string;
  showSearch?: boolean;
  orgData?: any;
  isBookshelfMode?: boolean;
}

const EquipmentsCandidatesModal: React.FC<EquipmentsCandidatesModalProps> = ({
  isOpen,
  onClose,
  title,
  equipments,
  pieceOptions,
  onAddEquipments,
  emptyMessage = "Aucun équipement trouvé",
  addButtonText = "Ajouter les équipements sélectionnés",
  showSearch = false,
  orgData,
  isBookshelfMode = false
}) => {
  const [selectedEquipments, setSelectedEquipments] = useState<Set<number>>(new Set());
  const [equipmentSelectionData, setEquipmentSelectionData] = useState<Record<number, EquipmentSelectionData>>({});
  const [searchValue, setSearchValue] = useState("");

  // Reset state when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      setSelectedEquipments(new Set());
      setEquipmentSelectionData({});
      setSearchValue("");
    }
  }, [isOpen]);

  const handleToggleEquipmentSelection = (index: number) => {
    const newSelected = new Set(selectedEquipments);
    const newSelectionData = { ...equipmentSelectionData };
    
    if (newSelected.has(index)) {
      newSelected.delete(index);
      delete newSelectionData[index];
    } else {
      newSelected.add(index);
      // Initialize with default values
      newSelectionData[index] = {
        piece: pieceOptions.length > 0 ? pieceOptions[0].value : "",
        equipment_type: isBookshelfMode ? (equipments[index]?.equipment_type || "") : ""
      };
    }
    
    setSelectedEquipments(newSelected);
    setEquipmentSelectionData(newSelectionData);
  };

  const handleUpdateEquipmentData = (index: number, field: keyof EquipmentSelectionData, value: string) => {
    setEquipmentSelectionData(prev => ({
      ...prev,
      [index]: {
        ...prev[index],
        [field]: value
      }
    }));
  };

  const handleAddSelectedEquipments = async () => {
    if (selectedEquipments.size === 0) return;
    
    const equipmentsToAdd = Array.from(selectedEquipments).map(index => ({
      equipment: equipments[index],
      selectionData: equipmentSelectionData[index]
    }));
    
    await onAddEquipments(equipmentsToAdd);
    onClose();
  };

  const isAddButtonDisabled = 
    selectedEquipments.size === 0 ||
    Array.from(selectedEquipments).some(index => {
      const data = equipmentSelectionData[index];
      return !data?.piece || !data?.equipment_type || 
             (data.piece === 'custom' && !data.customPieceName);
    });

  // Filter equipments based on search value
  const filteredEquipments = equipments.filter(equipment => {
    if (!searchValue) return true;
    const searchLower = searchValue.toLowerCase();
    return (
      equipment.name?.toLowerCase().includes(searchLower) ||
      equipment.brand?.toLowerCase().includes(searchLower) ||
      equipment.category?.toLowerCase().includes(searchLower) ||
      equipment.label?.toLowerCase().includes(searchLower) ||
      (isBookshelfMode && equipment.model?.toLowerCase().includes(searchLower)) ||
      (isBookshelfMode && equipment.equipment_type?.toLowerCase().includes(searchLower))
    );
  });

  const handleEquipmentClick = (equipmentId: string) => {
    // Find the equipment in the filtered list and get its original index
    const originalIndex = equipments.findIndex(eq => eq.id === equipmentId);
    if (originalIndex !== -1) {
      handleToggleEquipmentSelection(originalIndex);
    }
  };

  return (
    <Modal1 isOpen={isOpen} onClose={onClose}>
      <div className="bg-white rounded-xl w-c800 h-fit gap-2 flex flex-col p-4">
        <div className="text-h1 mb-4 font-medium text-black">
          {title}
        </div>
        
        {showSearch && orgData && (
          <div className="mb-4">
            <SearchBarDocuments
              orgData={orgData}
              options={equipments.map(equipment => ({
                label: `${equipment.name || ''} - ${equipment.brand || ''} ${equipment.model || ''}`.trim().replace(/^-\s*/, ''),
                id: equipment.id,
                equipment: equipment
              }))}
              value={searchValue}
              setValue={setSearchValue}
              onAction={(selectedOption: any) => {
                if (selectedOption && selectedOption.id) {
                  handleEquipmentClick(selectedOption.id);
                }
              }}
              setSeachType={(type: any) => {}}
              placeholder="Rechercher un équipement ..."
            />
          </div>
        )}
        
        {filteredEquipments.length === 0 ? (
          <div className="text-center py-8">
            <div className="text-h3 text-gray-500">
              {searchValue ? "Aucun équipement trouvé pour cette recherche" : emptyMessage}
            </div>
          </div>
        ) : (
          <>
            <div className="text-h3 mb-4 text-gray-600">
              {filteredEquipments.length} équipement(s) trouvé(s). Sélectionnez ceux que vous voulez ajouter :
            </div>
            
            <div className="max-h-96 overflow-y-auto">
              <div className="grid grid-cols-1 gap-3">
                {filteredEquipments.map((equipment, filteredIndex) => {
                  // Get the original index of this equipment in the full list
                  const originalIndex = equipments.findIndex(eq => eq === equipment);
                  return (
                  <div
                    key={originalIndex}
                    onClick={() => handleToggleEquipmentSelection(originalIndex)}
                    className={`
                      border rounded-lg p-4 cursor-pointer transition-all duration-200
                      ${selectedEquipments.has(originalIndex)
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300 bg-white'
                      }
                    `}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3">
                          <div
                            className={`
                              w-5 h-5 rounded border-2 flex items-center justify-center
                              ${selectedEquipments.has(originalIndex)
                                ? 'border-blue-500 bg-blue-500'
                                : 'border-gray-300'
                              }
                            `}
                          >
                            {selectedEquipments.has(originalIndex) && (
                              <Icon1 icon_name="done2" size={12} fill="white" />
                            )}
                          </div>
                          
                          <div className="flex-1">
                            <div className="text-h3 font-semibold text-black">
                              {equipment.name || 'Équipement sans nom'}
                            </div>
                            <div className="text-h4 text-gray-600 mt-1">
                              <span className="font-medium">Marque:</span> {equipment.brand || '-'}
                              {isBookshelfMode ? (
                                <>
                                  {equipment.model && (
                                    <>
                                      <span className="font-medium ml-2">•</span>
                                      <span className="font-medium ml-2">Modèle:</span> {equipment.model}
                                    </>
                                  )}
                                  <span className="font-medium ml-2">•</span>
                                  <span className="font-medium ml-2">Catégorie:</span> {equipment.equipment_type || '-'}
                                </>
                              ) : (
                                <>
                                  <span className="font-medium ml-2">•</span>
                                  <span className="font-medium ml-2">Catégorie:</span> {equipment.category || '-'}
                                  {equipment.label && (
                                    <>
                                      <span className="font-medium ml-2">•</span>
                                      <span className="font-medium ml-2">Label:</span> {equipment.label}
                                    </>
                                  )}
                                </>
                              )}
                            </div>
                            
                            {selectedEquipments.has(originalIndex) && (
                              <div 
                                className="mt-3 grid grid-cols-2 gap-3"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <div>
                                  <DropDownGPT03
                                    label="Pièce"
                                    value={equipmentSelectionData[originalIndex]?.piece || ""}
                                    onChange={(item) => handleUpdateEquipmentData(originalIndex, 'piece', item.value as string)}
                                    items={[
                                      ...pieceOptions.map(p => ({ label: p.label, value: p.value })),
                                      { label: "Autre (saisir manuellement)", value: "custom" }
                                    ]}
                                    mandatory={true}
                                  />
                                  {equipmentSelectionData[originalIndex]?.piece === 'custom' && (
                                    <div className="mt-2">
                                      <TextField2
                                        value={equipmentSelectionData[originalIndex]?.customPieceName || ""}
                                        onChange={(e) => handleUpdateEquipmentData(originalIndex, 'customPieceName', e.target.value)}
                                        label="Nom de la pièce"
                                      />
                                    </div>
                                  )}
                                </div>
                                
                                <div>
                                  <EquipmentTypeSelect
                                    label="Type d'équipement"
                                    value={equipmentSelectionData[originalIndex]?.equipment_type || ""}
                                    onChange={(value: string) => handleUpdateEquipmentData(originalIndex, 'equipment_type', value)}
                                    mandatory={true}
                                  />
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  );
                })}
              </div>
            </div>
            
            <div className="flex justify-between items-center mt-6 pt-4 border-t">
              <div className="text-h4 text-gray-600">
                {selectedEquipments.size} équipement(s) sélectionné(s)
              </div>
              <div className="flex gap-2">
                <SecondaryButton onClick={onClose}>
                  Annuler
                </SecondaryButton>
                <PrimaryButton 
                  onClick={handleAddSelectedEquipments}
                  disabled={isAddButtonDisabled}
                >
                  {addButtonText}
                </PrimaryButton>
              </div>
            </div>
          </>
        )}
      </div>
    </Modal1>
  );
};

export default EquipmentsCandidatesModal;